// src/services/review-service.js
// Client-side service for the Reviews System.
// Reviews live in top-level `reviews/{reviewId}` collection.
// reviewStats (averageRating, totalReviews, ratingBreakdown) live on practitioners/{uid}.reviewStats.

import { db } from '../config/firebase-config';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'us-west1';

// ─── Real-time subscriptions ─────────────────────────────────────────────────

/**
 * Subscribe to all reviews for a practitioner.
 * Returns unsubscribe function.
 * @param {string} practitionerId
 * @param {function} onReviews - called with reviews array on each change
 */
export function subscribeToReviews(practitionerId, onReviews) {
  if (!practitionerId || !db) return () => {};
  // No orderBy — avoids need for a composite Firestore index that may not exist.
  // Reviews are sorted client-side by submittedAt (nulls last for pending reviews).
  const q = query(
    collection(db, 'reviews'),
    where('practitionerId', '==', practitionerId)
  );

  return onSnapshot(q, (snap) => {
    const reviews = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const ta = a.submittedAt?.toDate?.()?.getTime() ?? a.requestedAt?.toDate?.()?.getTime() ?? 0;
        const tb = b.submittedAt?.toDate?.()?.getTime() ?? b.requestedAt?.toDate?.()?.getTime() ?? 0;
        return tb - ta;
      });
    onReviews(reviews);
  }, (err) => {
    console.error('subscribeToReviews error:', err.message);
  });
}

// ─── Practitioner actions (callable Cloud Functions) ─────────────────────────

/**
 * Respond publicly to a review.
 * @param {string} reviewId
 * @param {string} response - practitioner's public reply text
 */
export async function respondToReview(reviewId, response) {
  const functions = getFunctions(undefined, REGION);
  const fn = httpsCallable(functions, 'respondToReview');
  const result = await fn({ reviewId, response });
  return result.data;
}

/**
 * Flag a review for admin attention.
 * @param {string} reviewId
 * @param {string} reason - reason category (e.g. 'inappropriate_content')
 * @param {string} [explanation] - optional freetext explanation (required for 'other')
 */
export async function flagReview(reviewId, reason, explanation) {
  const functions = getFunctions(undefined, REGION);
  const fn = httpsCallable(functions, 'flagReview');
  const result = await fn({ reviewId, reason, explanation: explanation || null });
  return result.data;
}

/**
 * Manually request a review for a specific booking.
 * Sends a one-off review request email to the client.
 * @param {string} bookingId
 */
export async function requestManualReview(bookingId) {
  const functions = getFunctions(undefined, REGION);
  const fn = httpsCallable(functions, 'requestManualReview');
  const result = await fn({ bookingId });
  return result.data;
}

// ─── Public read (for public website) ────────────────────────────────────────

/**
 * Get published reviews for a practitioner (one-time fetch).
 * Used by PublicWebsite.js (fire-and-forget after page load).
 * @param {string} practitionerId
 * @returns {Promise<Array>}
 */
export async function getPublishedReviews(practitionerId) {
  // No orderBy — avoids composite index requirement. Sorted client-side.
  const q = query(
    collection(db, 'reviews'),
    where('practitionerId', '==', practitionerId),
    where('status', '==', 'published')
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const ta = a.submittedAt?.toDate?.()?.getTime() ?? 0;
      const tb = b.submittedAt?.toDate?.()?.getTime() ?? 0;
      return tb - ta;
    });
}
